import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { Typography, Paper, Box, Button, MenuItem, TextField, Alert } from '@mui/material';

const approveAdminRequest = httpsCallable(functions, 'approveAdminRequest');
const denyAdminRequest = httpsCallable(functions, 'denyAdminRequest');

const ROLE_OPTIONS = ['VIEWER', 'VIEWER_LOCALITY', 'EDITOR', 'ADMIN'];

// Lists pending dashboard access requests (adminRequests/{uid} where
// status == 'pending', filed automatically when someone signs in - Google or
// email/password - without a role yet) and lets ADMIN/OWNER approve (granting
// a role via the approveAdminRequest Cloud Function) or deny them.
export default function AccessRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [roleChoice, setRoleChoice] = useState({});
  const [localityChoice, setLocalityChoice] = useState({});
  const [busyUid, setBusyUid] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'adminRequests'), where('status', '==', 'pending'));
    return onSnapshot(q, (snap) => {
      setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error('Failed to load access requests:', error);
      setMessage({ type: 'error', text: error.message });
    });
  }, []);

  async function handleApprove(uid) {
    setBusyUid(uid);
    setMessage(null);
    try {
      const role = roleChoice[uid] || 'VIEWER';
      const locality = role === 'VIEWER_LOCALITY' ? (localityChoice[uid] || '').trim() : undefined;
      if (role === 'VIEWER_LOCALITY' && !locality) {
        setMessage({ type: 'error', text: 'Enter a locality for VIEWER_LOCALITY.' });
        return;
      }
      await approveAdminRequest({ uid, role, locality });
      setMessage({ type: 'success', text: role === 'VIEWER_LOCALITY' ? `Granted VIEWER_LOCALITY access for ${locality}.` : `Granted ${role} access.` });
    } catch (error) {
      console.error('Failed to approve request:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyUid(null);
    }
  }

  async function handleDeny(uid) {
    setBusyUid(uid);
    setMessage(null);
    try {
      await denyAdminRequest({ uid });
      setMessage({ type: 'success', text: 'Request denied.' });
    } catch (error) {
      console.error('Failed to deny request:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyUid(null);
    }
  }

  return (
    <Box sx={{ maxWidth: 700 }}>
      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}
      {requests.length === 0 ? (
        <Typography color="text.secondary">No pending access requests.</Typography>
      ) : requests.map((req) => (
        <Paper key={req.id} variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography>{req.displayName ? `${req.displayName} (${req.email})` : req.email}</Typography>
            <Typography variant="caption" color="text.secondary">uid: {req.id}</Typography>
          </Box>
          <TextField
            select size="small" label="Role" sx={{ width: 160 }}
            value={roleChoice[req.id] || 'VIEWER'}
            onChange={(e) => setRoleChoice((r) => ({ ...r, [req.id]: e.target.value }))}
          >
            {ROLE_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          {roleChoice[req.id] === 'VIEWER_LOCALITY' && (
            <TextField
              size="small" label="Locality" sx={{ width: 160 }}
              value={localityChoice[req.id] || ''}
              onChange={(e) => setLocalityChoice((l) => ({ ...l, [req.id]: e.target.value }))}
            />
          )}
          <Button
            variant="contained" disabled={busyUid === req.id || (roleChoice[req.id] === 'VIEWER_LOCALITY' && !localityChoice[req.id]?.trim())}
            onClick={() => handleApprove(req.id)}
          >
            Approve
          </Button>
          <Button color="error" disabled={busyUid === req.id} onClick={() => handleDeny(req.id)}>
            Deny
          </Button>
        </Paper>
      ))}
    </Box>
  );
}
